import { ReactElement } from "react";
import { Flex, Grid, GridProps, Icon, Text } from "@chakra-ui/react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import dayjs from "dayjs";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { CategoryMark } from "./CategoryMark";
import {
  categoryOf,
  formatNotifiedAt,
  Measurement,
  measurementsOf,
  NotificationItem,
} from "./notificationTypes";

type Translate = ReturnType<typeof useModifiedTranslation>;

function headingOf(item: NotificationItem, t: Translate): string {
  if (item.kind === "alert") {
    return t(`Notification.heading.${item.alertType}`);
  }
  // 単数・複数は count で選び、文には days を出す。日数が分からなければ「-」を出す
  const days = item.reminderInfo?.days_since_last_analysis;
  return t("Notification.heading.reminder", {
    count: days ?? 0,
    days: days ?? "-",
  });
}

// 開いた行の値。最終測定日は今年でも年を出す（V9）
function valueOf(measurement: Measurement, t: Translate): string {
  switch (measurement.kind) {
    case "lastAnalyzedAt":
      return dayjs(measurement.at).format("YYYY/MM/DD");
    case "mentalActivity":
      return (measurement.value * 100).toFixed(1);
    case "recentCount":
    case "priorCount":
      return t("Notification.measurement.count", { count: measurement.value });
    case "daysSinceLastAnalysis":
      return t("Notification.measurement.days", { count: measurement.value });
    case "mindIndex":
      return String(measurement.value);
  }
}

/*
 * 通知一覧の1行。種別の丸・日時・見出しを横に並べ、右端に開閉の矢印を置く。押すとその場で開く。
 * 日時の幅を64pxにそろえて、見出しの頭を行ごとにそろえる。
 * アプリの既定の太字では日時が64pxに収まらないので、日時と見出しは通常の太さにする
 */
export function NotificationRow(
  props: Readonly<{
    item: NotificationItem;
    /** 開いているか。開けるのは一覧で1行だけ */
    isOpen: boolean;
    /** 行を押したとき。開いていれば閉じ、閉じていれば開く */
    onToggle: () => void;
    testId?: string;
  }>,
): ReactElement {
  const { item, isOpen, onToggle, testId } = props;
  const t = useModifiedTranslation();

  const cells = (
    <>
      <CategoryMark category={categoryOf(item)} size="row" />
      <Text
        fontSize="12px"
        fontWeight="normal"
        color="text.sub_text"
        whiteSpace="nowrap"
      >
        {formatNotifiedAt(item, new Date())}
      </Text>
      <Text fontSize="15px" fontWeight="normal" noOfLines={2}>
        {headingOf(item, t)}
      </Text>
    </>
  );

  // 出す値が無い行は開けない。ボタンにせず、矢印も出さない
  const measurements = measurementsOf(item);
  if (measurements.length === 0) {
    return (
      <Grid {...ROW_LAYOUT} data-testid={testId}>
        {cells}
      </Grid>
    );
  }
  return (
    <Grid
      as="button"
      type="button"
      {...ROW_LAYOUT}
      aria-expanded={isOpen}
      onClick={onToggle}
      data-testid={testId}
    >
      {cells}
      {/* 開いたときは、見出しと値の2段の真ん中に置く */}
      <Icon
        as={isOpen ? IoChevronUp : IoChevronDown}
        boxSize={4}
        color="gray.400"
        gridColumn={4}
        gridRow="1 / span 2"
        alignSelf="center"
      />
      {/* 値は見出しの頭にそろえ、枠を付けずに横に並べる。幅が足りなければ折り返す */}
      {isOpen && (
        <Flex
          gridColumn={3}
          wrap="wrap"
          columnGap={5}
          rowGap={1}
          mt={1}
          data-testid="notification-measurements"
        >
          {measurements.map((measurement) => (
            <Flex key={measurement.kind} align="baseline" gap={2}>
              <Text fontSize="11px" fontWeight="normal" color="text.sub_text">
                {t(`Notification.measurement.${measurement.kind}`)}
              </Text>
              <Text fontSize="15px" fontWeight="bold">
                {valueOf(measurement, t)}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Grid>
  );
}

// 開ける行も開けない行も、同じ列にそろえる。4列目は開閉の矢印
const ROW_LAYOUT: GridProps = {
  w: "full",
  textAlign: "left",
  templateColumns: "20px 64px 1fr 16px",
  columnGap: 2,
  alignItems: "baseline",
  py: 3,
  borderBottomWidth: "1px",
  borderColor: "chakra-border-color",
};
